<template>
    <!-- Noeud en cours avec ses enfants -->
    <li class="Node" :class="(SelectMode == selectModeEnum.MultiAdvanced && Node.CheckLeader) ? 'Selected': ''">

        <!-- Seulement la ligne de noeud -->
        <div v-show="!Node.Hidden" 
            v-on="Node.Disabled ? {} : (SelectMode == selectModeEnum.MultiAdvanced || SelectMode == selectModeEnum.MultiSimple) && Node.WithCheckBox ? { click: () => toggleSelectedNode(Root, Node) } : { click: () => setSelectedNode() }"
            class="NodeInfoLine text-nowrap" :class="(SelectMode === selectModeEnum.Single && Node.Id === getSelectedNode().Id) ? `text-${Variant} bg-${Variant}-bg-subtle`: ''">

            <!-- Icone sur la gauche du noeud pour déployer ou replier les noeuds enfants -->
            <span v-if="Node.RightBound - Node.LeftBound > 1 && !(Node.HiddenCaret)" @click.stop.prevent="toggleExpandNode(Node)"
                style="display: inline-block;" :style="SelectMode == selectModeEnum.MultiAdvanced && Node.NestedChecked ? 'color: var(--' + Variant + ');' : ''">
                <!-- v-bind:class="openerClass" -->
                <b-icon v-if="Node.Expanded" icon="caret-down-fill" :variant="SelectMode === selectModeEnum.Single && Node.Id === getSelectedNode().Id ? Variant : 'gray-dark'" class="mr-1 mb-1 align-middle" />
                <b-icon v-else icon="caret-right-fill" variant="gray-dark" class="mr-1 mb-1 align-middle" />
            </span>
            <span v-else class="mr-1"><b-icon /></span>

            <!-- Case à cocher liée aux filtres -->
            <b-form-checkbox v-if="(SelectMode == selectModeEnum.MultiAdvanced || SelectMode == selectModeEnum.MultiSimple) && Node.WithCheckBox" inline plain
                :checked="Node.Checked" :indeterminate="Node.Indeterminate"
                :disabled="(SelectMode == selectModeEnum.MultiAdvanced || SelectMode == selectModeEnum.MultiSimple) && Node.Disabled"
                class="m-0 p-0" :class="Node.NestedChecked ? 'nestedChecked' : ''" />

            <span :class="['p-0 text-nowrap d-inline-block mw-100', { 'text-gray-dark': (Node.Disabled && Node.WithCheckBox)  }
                    , (SelectMode === selectModeEnum.Single && Node.Id === getSelectedNode().Id) ? 'text-' + Variant + ' bg-' + Variant + '-bg-subtle' : '']">
                <div class="p-0 m-0"
                    :style="SelectMode == selectModeEnum.MultiAdvanced && Node.NestedChecked ? 'color: var(--' + Variant + ');' : ''">
                    <b-icon v-if="UseBootstrapIcon" :icon="Node.Icon" class="ml-0 mr-1"/>
                    <b-img v-else :src="AbsoluteLink(Node.Icon)" class="ml-0 mr-1" />
                    {{ Node.Name }}
                </div>
            </span>
        </div>
        <!-- Liste des noeuds enfant du noeud courant -->
        <transition name="node-opening">
            <ul v-if="Node.Expanded || Node.Forced">
                <!-- v-on="$listeners" utilisé pour passer les events à Heritage-Node à travers la hiérarchie d'HeritageNode -->
                <generic-node-view v-for="nodeChild in Node.ChildNodes" :key="$id(nodeChild.Id)" :Root="Root" :Node="nodeChild" :SelectMode="SelectMode" v-on="$listeners" :UseBootstrapIcon="UseBootstrapIcon"/>
            </ul>
        </transition>
    </li>
</template>

<script lang="ts">
import { Prop, InjectReactive } from 'vue-property-decorator';
import SelectModeEnum from '@/enums/SelectModeEnum';
import { LinkUtils, ObjectUtils } from '@/components/commons/Utils';
import Component, { mixins } from 'vue-class-component';
import { GenericTree, GenericTreeNode } from './GenericTreeClass';
import GenericTreeUtils from './GenericTreeUtils';

@Component({
    name: "generic-node-view",
    components:{ 
       GenericNodeView
    },
})
export default class GenericNodeView extends mixins(GenericTreeUtils,LinkUtils, ObjectUtils) {
    /** Nécessaire pour pouvoir utiliser cet enum dans la section template */
    private selectModeEnum = SelectModeEnum;

    /** Noeud courant */
    @Prop({ default: undefined })
    public Node!: GenericTreeNode;

    /** 
     * Variant de couleur à appliquer sur le composant
     * @default secondary
     */
    @InjectReactive()
    public Variant!: string;

    /** Racine de l'arbre contenant les métadonnées de l'arbre (count, selected, ...) */
    @InjectReactive()
    public Root!: GenericTree;

    /** Détermine si l'on doit utiliser des icones bootstrap */
    @InjectReactive()
    public UseBootstrapIcon!: boolean;

    /** 
     * Mode de selection
     * @returns {SelectModeEnum.Single|SelectModeEnum.MultiAdvanced|SelectModeEnum.MultiSimple}
     */
    @InjectReactive()
    public SelectMode!: SelectModeEnum;

    /** Obtient le noeud sélectionné dans les métadonnées de l'arbre */
    private getSelectedNode(): GenericTreeNode {
        return this.Root.CurrentSelectedNode;
    }

    /** Clé utilisée pour forcer le rafraichissement de la balise <component> pour rechargement de la fonction en cours d'utilisation */
    private lastNodeClickTime: Date = undefined; 

    /** Défini le noeud sélectionné */
    private setSelectedNode() {
        /** S'il s'agit du même noeud alors on ne le resélectionne pas pour éviter le rafraichissement de la fonction en cours d'utilisation */
        if ((this.getSelectedNode()?.Id === this.Node.Id))
            return;

        let newNodeClickTime: Date = new Date();

        if (!this.lastNodeClickTime || ((newNodeClickTime.getTime() - this.lastNodeClickTime.getTime()) >= 2000)) {
            this.toggleSelectedNode(this.Root, this.Node);
            /** Event lever lors du SetSelectedNode */
            this.$emit("Node-Selected", { node: this.Node, scrollIntoView: false, watchSelectedNode: true, nodeSelectionTime: newNodeClickTime })
        }
        else
            console.warn("GenericTreeView - updateSingleNode() - Clic trop rapproché de 2 secondes. Il n'a pas été pris en compte !");

        this.lastNodeClickTime = newNodeClickTime;
    }

};
</script>

<style scoped>
    .Node {
        position: relative;
        display: block;
        box-sizing: border-box;
        padding-left: 10px;
        right: 0px;
        transition-duration: 0.15s;
        /* font-size: 0.64rem; */
        /* margin: 2px 0px 3px 2px; */
        border-right: 0px;
        border-radius: 3px;
        -webkit-touch-callout: none; /* iOS Safari */
        -webkit-user-select: none; /* Safari */
        -khtml-user-select: none; /* Konqueror HTML */
        -moz-user-select: none; /* Old versions of Firefox */
        -ms-user-select: none; /* Internet Explorer/Edge */
        user-select: none; /* Non-prefixed version, currently
        supported by Chrome, Edge, Opera and Firefox */
    }

    .Node img {
        display: inline-block;
        width: 20px;
        height: 20px;
        margin: 0px 10px 0px 0px;
    }


    .Node:hover {
        cursor: pointer;
    }

    .Node.Selected {
        border: 1px solid var(--Variant);
        margin-left: -1px;
    } 

    ul {
        box-sizing: border-box;
        /*padding-inline-start: 10px !important;*/
        writing-mode: horizontal-tb;
        padding-left: 10px !important;
        margin-bottom: 0px !important;
        width: 100%;
        height: 100%;
    }

    li {
        list-style: none;
        list-style-position: inside;
        border-top: 0px;
        border-bottom: 0px;
    }

    :deep(input[type=checkbox]:checked) {
        /** Change la couleur de la checkbox */
        accent-color: var(--Variant) !important;
    }

    :deep(.nestedChecked > input[type=checkbox]) {
        box-shadow: 0px 0px 0px 1px var(--Variant) !important;
    }

    .NodeInfoLine > * {
        vertical-align: middle;
        padding-top: 0px;
    }

    .NodeInfoLine:hover {
        color: var(--gray-dark);
        background-color: var(--gray);
    }

    .dragStartZone {
        background-color: var(--VariantBgSubtle) !important;
        color: var(--Variant) !important;
    }
</style>